import { ApolloLink, Observable } from "apollo-link";
import i18n from "../i18n";
import { isPlanError, reportPlanError } from "./plan-errors";

/*
 * Apollo link for plan-gate refusals (SAAS-REVAMP/DESIGN.md §8.1, §8.5). Mutations handle their
 * errors in each component, so a link is the only place every operation passes through.
 *
 * For each FEATURE_NOT_IN_PLAN / PLAN_LIMIT_REACHED / ACCOUNT_SUSPENDED error it
 *   - reports it (App.vue opens the upgrade dialog), and
 *   - replaces the server's English message with our translated one, since most components show
 *     graphQLErrors[0].message as-is.
 * A refusal the server sends with a 4xx status (an operation-level gate) is handed on as an
 * ordinary GraphQL error, so components treat it like any resolver error instead of a network
 * failure. Everything else passes through untouched.
 */

/** The text shown for a plan-gate error, in the current language. */
export function planErrorMessage(err) {
  if (err.type === "PLAN_LIMIT_REACHED") return i18n.t("saasPlan.limitReachedMessage");
  if (err.type === "ACCOUNT_SUSPENDED") return i18n.t("saasPlan.suspendedMessage");
  return i18n.t("saasPlan.featureNotInPlanMessage");
}

function operationTypeOf(operation) {
  const definitions = (operation && operation.query && operation.query.definitions) || [];
  for (let i = 0; i < definitions.length; i++) {
    if (definitions[i].kind === "OperationDefinition") return definitions[i].operation;
  }
  return "query";
}

export function createPlanErrorLink(translate = planErrorMessage) {
  const localize = (errors, operationType) =>
    errors.map(err => {
      if (!isPlanError(err)) return err;
      reportPlanError(err, { operationType });
      return { ...err, message: translate(err) || err.message, serverMessage: err.message };
    });

  return new ApolloLink(
    (operation, forward) =>
      new Observable(observer => {
        const operationType = operationTypeOf(operation);
        let subscription = null;
        try {
          subscription = forward(operation).subscribe({
            next: result => {
              if (result && Array.isArray(result.errors) && result.errors.some(isPlanError)) {
                result = { ...result, errors: localize(result.errors, operationType) };
              }
              observer.next(result);
            },
            error: networkError => {
              const body = networkError && networkError.result;
              const errors = body && Array.isArray(body.errors) ? body.errors : [];
              if (errors.length && errors.every(isPlanError)) {
                observer.next({ data: null, errors: localize(errors, operationType) });
                observer.complete();
                return;
              }
              observer.error(networkError);
            },
            complete: () => observer.complete(),
          });
        } catch (e) {
          observer.error(e);
        }
        return () => {
          if (subscription) subscription.unsubscribe();
        };
      })
  );
}
